/* Progressive enhancement only. Shopify owns pricing, variants, and checkout. */
(() => {
  'use strict';
  class ProductEdit extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.cards = [...this.querySelectorAll('[data-tm-fabric]')];
      this.buttons = [...this.querySelectorAll('[data-tm-filter]')];
      if (!this.cards.length || !this.buttons.length) return;
      this.controller = new AbortController();
      this.querySelector('[data-tm-tabs]').hidden = false;
      this.buttons.forEach(button => button.addEventListener('click', () => this.filter(button.dataset.tmFilter, true), {signal: this.controller.signal}));
      this.filter('all', false);
    }
    filter(value, announce) {
      // Fall back to all products if the merchant removes an entire fabric group.
      if (value !== 'all' && !this.cards.some(card => card.dataset.tmFabric === value)) value = 'all';
      let count = 0;
      this.cards.forEach(card => {
        card.hidden = value !== 'all' && card.dataset.tmFabric !== value;
        if (!card.hidden) count++;
      });
      this.buttons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.tmFilter === value)));
      if (announce) this.querySelector('[data-tm-status]').textContent = `${count} products shown`;
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; }
  }
  class ComfortFinder extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.results = [...this.querySelectorAll('[data-tm-result]')];
      if (!this.results.length) return; // Preserve the linked size guide if unconfigured.
      this.controller = new AbortController();
      this.querySelector('[data-tm-controls]').hidden = false;
      this.querySelector('[data-tm-results]').hidden = false;
      this.querySelector('[data-tm-fallback]').hidden = true;
      this.addEventListener('change', () => this.update(), {signal: this.controller.signal});
      this.update();
    }
    update() {
      this.results = [...this.querySelectorAll('[data-tm-result]')];
      const selections = [...this.querySelectorAll('input:checked')];
      const size = selections.find(input => input.name.startsWith('tm-size-'))?.value;
      const fabric = selections.find(input => input.name.startsWith('tm-fabric-'))?.value;
      let matched = false;
      this.results.forEach(result => {
        result.hidden = result.dataset.size !== size || result.dataset.fabric !== fabric;
        if (!result.hidden) matched = true;
      });
      this.querySelector('[data-tm-no-match]').hidden = matched;
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; }
  }
  if (!customElements.get('tm-product-edit')) customElements.define('tm-product-edit', ProductEdit);
  if (!customElements.get('tm-comfort-finder')) customElements.define('tm-comfort-finder', ComfortFinder);
})();

/* Product information: linked content without JS, keyboard-accessible tabs with JS. */
(() => {
  class InformationTabs extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.tabs = [...this.querySelectorAll('[data-tm-tab]')];
      this.panels = this.tabs.map(tab => this.querySelector(tab.getAttribute('href')));
      if (this.tabs.length < 2 || this.panels.some(panel => !panel)) return;
      this.controller = new AbortController();
      const opts = {signal: this.controller.signal};
      this.querySelector('[data-tm-tablist]').setAttribute('role', 'tablist');
      this.tabs.forEach((tab, index) => {
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', this.panels[index].id);
        this.panels[index].setAttribute('role', 'tabpanel');
        this.panels[index].tabIndex = 0;
        tab.addEventListener('click', event => {event.preventDefault(); this.select(index);}, opts);
        tab.addEventListener('keydown', event => {
          const last = this.tabs.length - 1;
          let next = index;
          if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
          else if (event.key === 'ArrowLeft') next = (index + last) % this.tabs.length;
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = last;
          else return;
          event.preventDefault(); this.select(next); this.tabs[next].focus();
        }, opts);
      });
      const selected = this.tabs.findIndex(tab => tab.hash === window.location.hash);
      this.select(selected < 0 ? 0 : selected);
      this.classList.add('is-enhanced');
    }
    select(index) {
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
        this.panels[i].hidden = i !== index;
      });
    }
    disconnectedCallback() {this.controller?.abort(); this.controller = null;}
  }
  if (!customElements.get('tm-information-tabs')) customElements.define('tm-information-tabs', InformationTabs);
})();

/* Video posters stay lightweight; playback starts only after a customer action. */
(() => {
  class FilmPlayer extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.video = this.querySelector('video');
      const button = this.querySelector('[data-tm-play]');
      if (!this.video || !button) return;
      this.controller = new AbortController();
      const opts = {signal: this.controller.signal};
      const error = this.querySelector('[data-tm-video-error]');
      const showPaused = () => {button.hidden = false; this.classList.remove('is-playing');};
      button.hidden = false;
      button.addEventListener('click', () => {
        error.hidden = true;
        this.video.play().catch(() => {showPaused();});
      }, opts);
      this.video.addEventListener('play', () => {
        document.querySelectorAll('tm-film-player video').forEach(video => {if (video !== this.video && !video.paused) video.pause();});
        button.hidden = true; this.classList.add('is-playing');
      }, opts);
      this.video.addEventListener('pause', showPaused, opts);
      this.video.addEventListener('ended', showPaused, opts);
      this.video.addEventListener('error', () => {button.hidden = true; error.hidden = false;}, opts);
    }
    disconnectedCallback() {this.video?.pause(); this.controller?.abort(); this.controller = null;}
  }
  if (!customElements.get('tm-film-player')) customElements.define('tm-film-player', FilmPlayer);
})();

/* Decorative hero video. No native or custom controls; pause outside the viewport. */
(() => {
  class HeroVideo extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.controller = new AbortController();
      const opts = {signal: this.controller.signal};
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = true;
      this.video.muted = true;
      this.video.controls = false;
      this.video.disablePictureInPicture = true;
      this.video.setAttribute('aria-hidden','true');
      this.video.tabIndex = -1;
      this.classList.add('is-enhanced');
      this.video.addEventListener('playing', () => this.classList.add('has-played'), opts);
      this.video.addEventListener('error', () => this.classList.remove('has-played'), opts);
      this.motion.addEventListener('change', () => this.reconcile(), opts);
      document.addEventListener('visibilitychange', () => this.reconcile(), opts);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {this.visible = entries[0].isIntersecting; this.reconcile();}, {threshold:.1});
        this.observer.observe(this);
      } else this.reconcile();
    }
    reconcile() {
      if (!this.visible || document.hidden || this.motion.matches || navigator.connection?.saveData) this.video.pause();
      else this.video.play().catch(() => {});
    }
    disconnectedCallback() {this.observer?.disconnect(); this.video?.pause(); this.controller?.abort(); this.controller=null;}
  }
  if (!customElements.get('tm-hero-video')) customElements.define('tm-hero-video', HeroVideo);
})();
